/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import yodaBridge from '@ks/yoda-js-sdk';
import Vue from 'vue';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import pick from 'lodash/pick';
import { Toast } from '@kibt/meta-ui';
import getLiveRoomInfo from './liveRoomInfo';
import { isYodaEnv, isInApp } from '@/utils/env';

import urls from '@/pages/worldcup/common/urls';

import { getQueryParams } from '@/utils/query';

const currentQuery = getQueryParams();

// #???
function proxyBridge(bridge: any): any {
    return new Proxy(bridge, {
        get() {
            return proxyBridge(() => {});
        },
        apply() {
            return Promise.resolve({});
        },
    });
}

const yoda = isYodaEnv ? yodaBridge : proxyBridge({});

(async () => {
    await yoda.ready();
    yoda.register({ namespace: 'component', name: 'liveStickerCompoundAndUpload' }); // 贴片合成&上传
    yoda.register({ namespace: 'component', name: 'liveDownloadFile' }); // 下载精彩瞬间视频
    yoda.register({ namespace: 'component', name: 'liveJumpToEdit' }); // 跳转编辑页
    yoda.register({ namespace: 'component', name: 'saveMediaToAlbum' }); // 保存到相册
    yoda.register({ namespace: 'component', name: 'liveCancelDownloadTask' }); // 取消保存到相册
    yoda.register({ namespace: 'component', name: 'openLiveProfile' }); // 客户端判断 半屏调mini profile页 全屏跳profile页
    yoda.register({ namespace: 'component', name: 'closeLiveHalfWeb' }); // 关闭半屏h5 isLiving=true
    yoda.register({ namespace: 'component', name: 'openLiveGiftPanel' }); // 直播间内 打开礼物面板
    yoda.register({ namespace: 'component', name: 'login' });
    yoda.register({ namespace: 'component', name: 'StopPullLoading' });
    yoda.register({ namespace: 'component', name: 'share' });
    yoda.register({ namespace: 'component', name: 'directShare' });
    yoda.register({ namespace: 'component', name: 'hasInstalledApp' });
    yoda.register({ namespace: 'component', name: 'bindPhone' });
    yoda.register({ namespace: 'component', name: 'liveAccess' });
    yoda.register({ namespace: 'component', name: 'selectCountryPhoneCode' });
    yoda.register({ namespace: 'component', name: 'getCurrentCountryCode' });
    yoda.register({ namespace: 'view', name: 'exitWebView' });
    yoda.register({ namespace: 'ui', name: 'setPhysicalBackButtonBehavior' });
    yoda.register({ namespace: 'tool', name: 'checkAppInstalled' });
    yoda.register({ namespace: 'view', name: 'requestDisallowInterceptTouchEvent' });
    yoda.register({ namespace: 'business', name: 'stopShowLoading' });
    yoda.register({ namespace: 'business', name: 'isCopaShowing' });
    yoda.ready().then(() => {
        // #???
        // 监听h5-closeAll-webview事件 当事件触发时关闭当前Webview
        yoda.event.addEventListener('h5-closeAll-webview', () => {
            let count = 0;
            const timer = setTimeout(() => {
                count++;
                if (count >= 10) {
                    clearTimeout(timer);
                }
                yoda.webview.close();
            });
        });
    });
})();

export { yoda };

/**
 *
 * @param url 打开的链接
 * @param options webview 相关的配置
 * @param query 需要拼接在url上的参数
 */
export function openWebview(url: string, query: Record<string, string | number> = {}, options?: webviewOptions) {
    const queryArr = Object.entries(query).map(([key, value]) => `${key}=${value}`);
    const queryStr = queryArr.join('&');
    const joiner = url.indexOf('?') !== -1 ? '&' : '?';
    if (queryStr) {
        url += joiner + queryStr;
    }

    if (url.startsWith('/')) {
        url = `${window.location.origin}${url}`;
    }
    if (!isYodaEnv) {
        window.location.href = url;
        return;
    }

    const defaultOptions = {
        titleColor: '#222222',
    };
    const launchOptions = { ...defaultOptions, ...options };
    yoda.ready().then(() => {
        yoda.webview?.open({
            url,
            launchOptions,
        });
    });
}

// https://docs.corp.kuaishou.com/d/home/fcAChw8A4YOTdl7ATr1RXdF22#
interface halfWebviewOptions {
    launchType: Number;
    url?: String;
    corner: Number;
    height: Number;
    hideToolbar: Boolean;
    fullScreen: String;
    enableProgress?:Boolean;
    title?: String; // 上推全屏的标题
}

/**
 *
 * @param url 打开的链接
 * @param options webview 相关的配置
 * @param query 需要拼接在url上的参数
 */
export function openHalfWebview(url: string, query: Record<string, string | number> = {}, options: halfWebviewOptions) {
    // 打开半屏默认拼接__launch_options__，fix iOS进度条和hideToolbar不生效的问题
    if (options.hideToolbar){
        query = { ...query, __launch_options__: '%7B%22topBarPosition%22%3A%22fixed%22%7D'};
    }
    const queryArr = Object.entries(query).map(([key, value]) => `${key}=${value}`);
    const queryStr = queryArr.join('&');
    const joiner = url.indexOf('?') !== -1 ? '&' : '?';
    if (queryStr) {
        url += joiner + queryStr;
    }

    if (url.startsWith('/')) {
        url = `${window.location.origin}${url}`;
    }
    if (!isYodaEnv) {
        window.location.href = url;
        return;
    }
    options.url = url;
    yoda.component?.loadNewUrlHalfPage(options);
}

export function openWebviewWithoutYoda(url: string) {
    if (url.startsWith('/')) {
        url = `${window.location.origin}${url}`;
    }
    const defaultOptions = {
        titleColor: '#222222',
    };
    const launchOptions = { ...defaultOptions };
    yoda.ready().then(() => {
        yoda.webview?.open({
            url,
            launchOptions,
        });
    });
}

/**
 * 打开新的页面
 * 若URL中存在Webview=yoda参数 则使用Yoda容器打开 若不存在则使用Ks容器打开
 * @param url 链接地址
 */
export const openStaticPage = (url: string) => {
    if (url.includes('webview=yoda')) {
        return openWebview(url);
    }

    window.location.href = url;
};

interface webviewOptions {
    title?: string; // 默认标题
    titleColor?: string; // title颜色
    topBarPosition?: string; // 顶部栏风格 isFullScreen ? fixed : default
    topBarBgColor?: string; // 顶部栏背景颜色
    topBarBorderColor?: string; // 顶栏下边线的颜色
    statusBarColorType?: string; // 电池栏文字颜色
    webviewBgColor?: string; // webview背景颜色
    onSlideBack?: string; // 右滑返回行为定义
}

export const setTitleColor = (color?: string) => {
    yoda.ui.hideLoadingPage();
    yoda.ui.setTitle({
        color: color || '#222222',
    });
};

type ToastType = 'normal' | 'success' | 'error';
export const showToast = (text: string, type: ToastType) => {
    if (!isYodaEnv) {
        console.log(text);
        return;
    }
    yoda.ready().then(() => {
        yoda.ui.showToast({ text, type });
    });
};

export const setFullScreen = () => {
    yoda.ui.setTopBarStyle({
        position: 'fixed',
    });
};

export const showErrorToast = (text?: string | { text?: string}) => {
    if (!!text && typeof text === 'object') {
        text = text.text || '';
    }
    const defaultText = typeof Vue.prototype.$k18n === 'function'
        ? Vue.prototype.$k18n({ id: 'common/network_error' })
        : '';
    Toast.show({
        text: text || defaultText,
    });
    if (!isYodaEnv) {
        console.log(text || defaultText);
    }
    // yoda.ready().then(() => {
    //     yoda.ui.showToast({ type: 'error', text: text || defaultText });
    // }).catch(() => {
    //     /* do nothing */
    // });
};

/**
 * 打开新的页面(更正)
 * 若URL为deeplink 则使用window.location跳转
 * 若URL为H5链接，则使用yoda容器打开
 * @param url 链接地址
 */
export const openPage = debounce((url: string, query: Record<string, string | number> = {}) => {
    if (!url) return;
    if (url.includes('ikwai://') || url.includes('ikwaibulldog://') || url.includes('kwai://')) {
        window.location.href = url;
        return;
    }
    openWebview(url, query);
}, 200, { leading: true, trailing: false });


const pathH5SrcMap = {
    [urls.home]: 'home',
    [urls.hometeam]: 'hometeam',
    [urls.hometeamChange]: 'hometeamChange',
    [urls.liveHome]: 'liveHome',
    [urls.matchHome]: 'matchHome',
    [urls.player]: 'player',
    [urls.team]: 'team',
};

// 单页面模式中打开新页面
const _openSpaPage = (url: string, params: Record<string, string> = {}) => {
    // if (!window.navigator.onLine) {
    //     showErrorToast(window.__$k18n({ id: 'kuaishan_template_fetch_failed'}));
    //     return;
    // }
    params = params ?? {};
    if (url.includes('ikwai://') || url.includes('ikwaibulldog://') || url.startsWith('http')) {
        window.location.href = url;
        return;
    }
    if (!params.__launch_options__) {
        // 这里是个坑，只能全编码，不能带特殊符号
        params.__launch_options__ = '%7B%22topBarPosition%22%3A%22fixed%22%7D';
    }
    if (!params.hyId) {
        params.hyId = 'worldcup%2Cworldcup2';
    }

    // 说明是自己跳自己，不是跳激励，跳积木那些
    const copiedQuery = pick(currentQuery, [
        'source',
        'noah_resource_id',
        'query_source_type',
        'query_name',
        'search_tab_name',
        'query_id',
        'subquery_id',
    ]) as Record<string, string>;

    Object.keys(copiedQuery).forEach(key => {
        // encode 一下
        copiedQuery[key] = encodeURIComponent(copiedQuery[key]);
    });
    params = { ...params, ...copiedQuery };

    try {
        const pathname = window.location.pathname;

        const h5Src = pathH5SrcMap[pathname];

        if (h5Src) {
            params.h5_src = h5Src;
        }
    } catch (error) {
        /**/
    }


    if (!params.bizId) {
        params.bizId = 'events';
    }
    openWebview(url, params);
};

export const openSpaPage = throttle(_openSpaPage, 1000);

// 打开新页面
export const openPageAction = (url:string, location = false) => {
    const {isLiving} = getLiveRoomInfo();
    if (isLiving && isInApp){
        if (location){
            // 在直播间&并且需要使用location打开
            window.location.href = url;
        } else {
            openHalfWebview(url, {},
                {
                    launchType: 1,
                    corner: 16,
                    height: 0.82,
                    fullScreen: '0',
                    hideToolbar: true,
                    enableProgress: true, // 安卓
                });
        }
    } else {
        openPage(url);
    }
};
